'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, BookOpen, ChevronRight } from 'lucide-react';
import Breadcrumbs from '@/components/common/Breadcrumbs';
import JsonLd from '@/components/common/JsonLd';
import { blogData } from '@/config/blogData';

// Extract blogs array from config data
const blogs = Object.values(blogData);

// Categories derived from blogs
const categories = ['All', 'Materials', 'Guides', 'Compliance', 'Technology'];

export default function BlogListingPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredBlogs = selectedCategory === 'All'
    ? blogs
    : blogs.filter(b => b.category === selectedCategory);

  // The first blog is designated as the "Featured Post"
  const featuredPost = blogs[0];
  const gridPosts = blogs.slice(1);

  // Filtered grid posts
  const filteredGridPosts = selectedCategory === 'All'
    ? gridPosts
    : filteredBlogs.filter(b => b.slug !== featuredPost.slug);

  const showFeaturedHeader = selectedCategory === 'All' && featuredPost;

  return (
    <main className="w-full bg-[#FAFCFF] min-h-screen pt-[72px] sm:pt-[80px] lg:pt-[100px]">
      <Breadcrumbs 
        items={[
          { name: "Blog", url: "/blog" }
        ]} 
      />
      
      <JsonLd 
        type="BreadcrumbList" 
        data={[
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" }
        ]} 
      />

      {/* Header Banner */}
      <section className="relative bg-[#050f19] text-white py-16 md:py-24 overflow-hidden border-b border-white/5">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"></div>
        
        <div className="w-full max-w-[1240px] mx-auto px-6 relative z-10 text-center md:text-left">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[#4fd1d3] text-xs font-bold tracking-[3px] uppercase mb-4">
              Insights & Knowledge
            </span>
            <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-bold font-serif mb-6 leading-tight tracking-tight">
              The Packaging & <br />
              <span className="bg-gradient-to-r from-blue-400 to-[#4fd1d3] bg-clip-text text-transparent font-sans font-extrabold">Labeling Resource Hub</span>
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              Stay ahead of product design, compliance standards, material innovations, and commercial printing technology. Expert analysis straight from our engineering team.
            </p>
          </div>
        </div>
      </section>

      {/* Main Blog Container */}
      <section className="py-16 md:py-24 w-full max-w-[1240px] mx-auto px-6">
        
        {/* Category Filters */}
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-12 border-b border-slate-200 pb-8">
          {categories.map((cat, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedCategory(cat)}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                selectedCategory === cat
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Featured Post (Only show on 'All' view) */}
        {showFeaturedHeader && (
          <div className="mb-16">
            <h2 className="text-sm font-bold text-blue-600 uppercase tracking-widest mb-6">Featured Article</h2>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.03)] border border-slate-100 grid grid-cols-1 lg:grid-cols-12 gap-8 hover:shadow-xl transition-all duration-500 group"
            >
              <div className="relative min-h-[300px] lg:col-span-7 bg-slate-100 overflow-hidden">
                <Image
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-6 left-6 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  {featuredPost.category}
                </div>
              </div>
              <div className="p-8 lg:p-12 lg:col-span-5 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-xs text-slate-500 mb-4 font-semibold">
                  <span className="flex items-center gap-1.5">
                    <Calendar size={14} />
                    {featuredPost.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock size={14} />
                    {featuredPost.readTime}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-snug group-hover:text-blue-600 transition-colors">
                  <Link href={`/blog/${featuredPost.slug}`}>
                    {featuredPost.title}
                  </Link>
                </h3>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                <div className="mt-auto">
                  <Link href={`/blog/${featuredPost.slug}`} className="inline-flex items-center gap-2 font-bold text-blue-600 group/btn">
                    Read Full Article
                    <ArrowRight size={18} className="transform group-hover/btn:translate-x-1.5 transition-transform" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Blog Post Grid */}
        <div>
          {showFeaturedHeader && (
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-8 border-t border-slate-100 pt-8">
              All Articles
            </h3>
          )}
          
          {filteredGridPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredGridPosts.map((post, idx) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-md border border-slate-100 hover:border-slate-200 transition-all duration-300 group flex flex-col h-full"
                >
                  <div className="relative h-56 bg-slate-100 overflow-hidden">
                    <Image
                      src={post.image}
                      alt={post.title}
                      fill
                      className="object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                      {post.category}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center gap-4 text-[11px] text-slate-500 mb-3 font-semibold">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {post.readTime}
                      </span>
                    </div>
                    <h4 className="text-lg font-bold text-slate-950 mb-3 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2">
                      <Link href={`/blog/${post.slug}`}>
                        {post.title}
                      </Link>
                    </h4>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto">
                      <Link href={`/blog/${post.slug}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 group/link">
                        Read Article
                        <ChevronRight size={14} className="transform group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-3xl border border-slate-100">
              <BookOpen size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 font-medium">No articles found in this category.</p>
            </div>
          )}
        </div>

      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-16 md:py-24 text-white text-center px-6 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-10 pointer-events-none" style={{ backgroundImage: "url('/images/story-hero.jpg')" }}></div>
        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center gap-6">
          <h2 className="text-white text-3xl md:text-4xl font-bold font-serif" style={{ color: '#ffffff' }}>Have a complex labeling challenge?</h2>
          <p className="text-slate-400 text-lg leading-relaxed max-w-xl">
            Our technical engineering team collaborates directly with procurement managers to design high-performance custom labeling formulations.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 mt-2">
            <Link href="/contact">
              <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full transition-all shadow-lg">
                Consult with our Engineers
              </button>
            </Link>
            <Link href="/quote">
              <button className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold px-8 py-4 rounded-full transition-all">
                Request custom samples
              </button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
